/**
 * Barramento simples para destinos STOMP adicionais (além de /topic/phoneStatus e /topic/greetings,
 * que continuam indo para o `wsStore`). A conexão única é a de `usePhoneManagerWs`; aqui só
 * repassamos o corpo bruto das mensagens para quem se inscreveu no destino.
 */

export const WS_DEST_USER_MESSAGES = "/user/queue/messages";
export const WS_DEST_OUTBOUND = "/topic/outbound";

/** Destinos assinados pela conexão principal e repassados ao barramento. */
export const WS_BUS_DESTINATIONS = [WS_DEST_USER_MESSAGES, WS_DEST_OUTBOUND] as const;

/** Estado do painel: broadcast legado (prod, `chamber.ws.noMemoryBroadcast=true`) e incrementais. */
export const WS_DEST_PHONE_STATUS = "/topic/phoneStatus";
/** Resposta do `/app/noMemory` só para esta sessão (backend sem o broadcast legado). */
export const WS_DEST_USER_PHONE_STATUS = "/user/queue/phoneStatus";
/** Atualizações de supervisores (backend sem o broadcast legado). */
export const WS_DEST_SUPERVISORS = "/topic/supervisors";

/**
 * Destinos que o backend (`StompSubscriptionGuard`) só deixa assinar com estes papéis. Assinar sem o
 * papel gera ERROR no STOMP e derruba a conexão (agente perde screen pop/mensagens) — então nem tentamos.
 */
export const WS_PRIVILEGED_ROLES = ["ROLE_ADMIN", "ROLE_SUPER", "ROLE_REPORT"];
export const WS_PRIVILEGED_DESTINATIONS: ReadonlySet<string> = new Set([WS_DEST_OUTBOUND, WS_DEST_SUPERVISORS]);

type Listener = (body: string) => void;

const listeners = new Map<string, Set<Listener>>();

export function onWsDestination(destination: string, listener: Listener): () => void {
  let set = listeners.get(destination);
  if (!set) {
    set = new Set();
    listeners.set(destination, set);
  }
  set.add(listener);
  return () => {
    set.delete(listener);
  };
}

export function emitWsDestination(destination: string, body: string) {
  const set = listeners.get(destination);
  if (!set) return;
  for (const l of set) {
    try {
      l(body);
    } catch {
      /* um listener com erro não derruba os demais */
    }
  }
}

/** Tenta interpretar o corpo como JSON (algumas mensagens são texto puro, ex.: "Recebido: ..."). */
export function parseWsJson(body: string): unknown {
  try {
    const v: unknown = JSON.parse(body);
    // Alguns conversores entregam o JSON como string JSON (duplamente codificado).
    if (typeof v === "string" && /^\s*[{[]/.test(v)) return JSON.parse(v);
    return v;
  } catch {
    return null;
  }
}
